import React, { useState, useCallback } from 'react';
import {
    Button,
    CardComponent,
    AlertComponent,
    InputComponent,
} from '../../../components/shared';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';

export type SSOLookupMode = 'domain' | 'tenantId';

export interface LoginProps {
    formMode: 'LOGIN' | 'CREATE_NEW_PASSWORD';
    title: string;
    errorMessage: string;
    passwordErrMsg: string;
    username: string;
    clickGate: boolean;
    onUserNameChange: (value: string) => void;
    password: string;
    onChangePassword: (value: string) => void;
    confirmPassword: string;
    onChangeConfirmPassword: (value: string) => void;
    onForgotPassword: () => void;
    onSubmit: () => void;
    ssoEnabled?: boolean;
    onSSOLogin?: (params: { domain?: string; tenantId?: string }) => void;
    onShowSSOForm?: () => void;
    showSSOForm?: boolean;
    onBackFromSSO?: () => void;
    tenantId?: string;
    onTenantIdChange?: (value: string) => void;
    domain?: string;
    onDomainChange?: (value: string) => void;
    ssoLookupMode?: SSOLookupMode;
    onSSOLookupModeChange?: (mode: SSOLookupMode) => void;
}

const Login: React.FC<LoginProps> = ({
    formMode = 'LOGIN',
    title = '',
    errorMessage = '',
    passwordErrMsg = '',
    username = '',
    clickGate = false,
    onUserNameChange = () => { },
    password = '',
    onChangePassword = () => { },
    confirmPassword = '',
    onChangeConfirmPassword = () => { },
    onForgotPassword = () => { },
    onSubmit = () => { },
    ssoEnabled = false,
    onSSOLogin,
    onShowSSOForm,
    showSSOForm = false,
    onBackFromSSO,
    tenantId = '',
    onTenantIdChange,
    domain = '',
    onDomainChange,
    ssoLookupMode = 'domain',
    onSSOLookupModeChange,
}) => {
    const { t } = useTranslation();
    /** Enter-to-submit only while focus is inside the login card (avoids firing on other routes e.g. SSO callback). */
    const handleFormKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key !== 'Enter' || event.defaultPrevented) return;
            const target = event.target as HTMLElement;
            if (target.closest('button, a[href], textarea')) return;
            event.preventDefault();
            onSubmit();
        },
        [onSubmit]
    );

    const [passwordVisible, setPasswordVisible] = useState(false); // shows the password field when 'eye' icon is clicked
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false); // shows the confirm password field when 'eye' icon is clicked

    // SSO lookup form view (domain-based default, with toggle to tenant ID)
    if (showSSOForm) {
        const isDomainMode = ssoLookupMode === 'domain';
        const currentValue = isDomainMode ? domain : tenantId;
        const canSubmit = Boolean(currentValue?.trim());

        const handleSSOSubmit = () => {
            if (!canSubmit || !onSSOLogin) return;
            if (isDomainMode) {
                const raw = domain.trim();
                const value = raw.includes('@') ? raw.split('@').pop()!.trim() : raw;
                if (!value) return;
                onSSOLogin({ domain: value });
            } else {
                onSSOLogin({ tenantId: tenantId.trim() });
            }
        };
        const handleSSOFormKeyDown = (event: React.KeyboardEvent) => {
            if (event.key !== 'Enter' || event.defaultPrevented) return;
            const target = event.target as HTMLElement;
            if (target.closest('button, a[href], textarea')) return;
            event.preventDefault();
            handleSSOSubmit();
        };
        return (
            <CardComponent title={t('auth.signInWithSSO')} className='login-card' onKeyDown={handleSSOFormKeyDown}>
                {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}
                {isDomainMode ? (
                    <div className='fieldView'>
                        <label className='labelView'>{t('auth.email')}</label>
                        <InputComponent
                            value={domain}
                            placeholder={t('auth.domainOrEmailPlaceholder')}
                            onChangeEvent={(e) => onDomainChange?.(e.target.value)}
                            disabled={clickGate}
                        />
                    </div>
                ) : (
                    <div className='fieldView'>
                        <label className='labelView'>{t('auth.tenantId')}</label>
                        <InputComponent
                            value={tenantId}
                            placeholder={t('auth.tenantIdPlaceholder')}
                            onChangeEvent={(e) => onTenantIdChange?.(e.target.value)}
                            disabled={clickGate}
                        />
                    </div>
                )}
                <div>
                    <div className='signBtnView'>
                        <Button
                            type='primary'
                            onClickEvent={handleSSOSubmit}
                            disabled={clickGate || !canSubmit}
                            className='SignBtn'
                        >
                            {t('auth.continueToSSO')}
                        </Button>
                    </div>
                    <div className='signBtnView'>
                        <Button
                            type='link'
                            onClickEvent={() => onSSOLookupModeChange?.(isDomainMode ? 'tenantId' : 'domain')}
                            className='forgetPassword'
                        >
                            {isDomainMode ? t('auth.useTenantIdInstead') : t('auth.useDomainInstead')}
                        </Button>
                    </div>
                    <div className='signBtnView'>
                        <Button type='link' onClickEvent={onBackFromSSO} className='forgetPassword'>
                            {t('auth.backToLogin')}
                        </Button>
                    </div>
                </div>
            </CardComponent>
        );
    }

    return (
        <CardComponent title={title} className='login-card' onKeyDown={handleFormKeyDown}>
            {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}
            {passwordErrMsg && <AlertComponent className='alertText' message={passwordErrMsg} type='error' />}
            <div className='fieldView'>
                <label className='labelView emaillable'>{t('auth.email')}</label>
                <InputComponent
                    value={username}
                    placeholder={t('auth.email')}
                    onChangeEvent={(e) => {
                        onUserNameChange(e.target.value);
                    }}
                    disabled={clickGate}
                />
            </div>
            <div className='fieldView'>
                <label className='labelView'>{formMode === 'LOGIN' ? t('auth.password') : t('auth.newPassword')}</label>
                <Input.Password
                    value={password}
                    placeholder={t('auth.password')}
                    onChange={(e) => onChangePassword(e?.target?.value)}
                    disabled={clickGate}
                    className='password'
                    visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
                />
            </div>
            {formMode === 'CREATE_NEW_PASSWORD' ? (
                <div className='fieldView'>
                    <label className='labelView'>{t('auth.confirmPassword')}</label>

                    <Input.Password
                        className='confirmPassword'
                        value={confirmPassword}
                        placeholder={t('auth.confirmPassword')}
                        onChange={(e) => onChangeConfirmPassword(e?.target?.value)}
                        visibilityToggle={{
                            visible: confirmPasswordVisible,
                            onVisibleChange: setConfirmPasswordVisible,
                        }}
                    />
                </div>
            ) : null}
            <div>
                {formMode === 'LOGIN' && (
                    <div className='signBtnView'>
                        <Button type='link' onClickEvent={onForgotPassword} className='forgetPassword'>
                            {t('auth.forgotPassword')}
                        </Button>
                    </div>
                )}
                <div className='signBtnView'>
                    <Button
                        type='primary'
                        onClickEvent={onSubmit}
                        disabled={clickGate}
                        className='SignBtn'
                    >
                        {formMode === 'CREATE_NEW_PASSWORD' ? t('auth.setPassword') : t('auth.login')}
                    </Button>
                </div>
                {formMode === 'LOGIN' && ssoEnabled && onShowSSOForm && (
                    <div className='ssoView' style={{ marginTop: 16 }}>
                        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-secondary, #666)', textAlign: 'center' }}>
                            {t('auth.orSignInWith')}
                        </div>
                        <Button
                            type='default'
                            onClickEvent={onShowSSOForm}
                            disabled={clickGate}
                            className='ssoBtn'
                            style={{ width: '100%' }}
                        >
                            {t('auth.signInWithSSO')}
                        </Button>
                    </div>
                )}
            </div>
        </CardComponent>
    );
};

export default Login;